<template>
  <v-dialog
    :model-value="open"
    content-class="show-modal-popup"
    persistent
    :scrim="'#212121'"
    :opacity="0.65"
    @update:model-value="$emit('update:open', $event)"
  >
    <div class="dialog-root">
      <div class="celebration-circles celebration-circles--fullscreen" aria-hidden="true">
        <span
          v-for="(circle, i) in circles"
          :key="i"
          class="celebration-circle"
          :style="{
            width: circle.size + 'px',
            height: circle.size + 'px',
            left: circle.left + '%',
            animationDuration: circle.duration + 's',
            animationDelay: circle.delay + 's',
            backgroundColor: circle.color
          }"
        />
      </div>
      <v-card class="pa-6 show-modal-popup show-modal-popup__card dialog-card">
        <v-card-title class="text-center pa-0">
        <!-- <icon
          class="d-block mx-auto"
          fill="success"
          height="56"
          width="56"
        >
          check-mark-circle
        </icon> -->
        <p class="text-6xl mb-4">🎉</p>
        <h3 class="text-3xl">BINGO!</h3>
      </v-card-title>
      <v-card-text class="text-center text-body-2 pa-0 mb-3">
        <p class="mb-2">{{ headlineText }}</p>
        <div v-for="prize in displayPrizes" :key="prize.bingo_number" class="prize-earned">
          <span class="prize-name">{{ prize.name }} Earned!</span>
        </div>

        <p v-if="displayPrizes.length > 0 && !continueToBlackout" class="claim-prize-text">{{ claimText }}</p>
      </v-card-text>
      <v-card-actions class="flex-column pa-0">
        <v-btn
          color="primary"
          variant="flat"
          size="large"
          rounded
          block
          class="mb-2 show-modal-popup__btn awesome-btn"
          @click="onPrimaryClick"
        >
        🎊 Awesome!
        </v-btn>
        
      </v-card-actions>
      </v-card>
    </div>
  </v-dialog>
</template>

<script>
export default {
  name: 'BingoWinDialog',
  props: {
    open: {
      type: Boolean,
      default: false
    },
    prizes: {
      type: Array,
      default: () => []
    },
    // Actual number of bingos completed in this check-in. Drives the headline
    // and claim-text pluralization independently from `prizes` so the user
    // sees "You completed 2 bingos!" even if only one rung has a configured
    // reward (e.g. multi-win on a partial ladder).
    newBingos: {
      type: Number,
      default: 0
    },
    // When true, primary action advances to the blackout modal instead of Rewards.
    continueToBlackout: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:open', 'continue'],
  data() {
    return {
      circles: []
    };
  },
  watch: {
    open: {
      handler(isOpen) {
        if (isOpen) this.circles = this.generateCircles();
      },
      immediate: true
    }
  },
  computed: {
    displayPrizes() {
      // No fallback to a placeholder 'Prize'. When the won bingo has no
      // configured ladder entry (e.g. user makes a 4th bingo on a 3-rung
      // ladder), we show the celebration but no prize line.
      return Array.isArray(this.prizes) ? this.prizes : [];
    },
    bingoCount() {
      // Fall back to displayPrizes.length for legacy callers that didn't pass
      // newBingos, but cap at 1 minimum so the dialog always reads as "won".
      return this.newBingos > 0 ? this.newBingos : Math.max(this.displayPrizes.length, 1);
    },
    headlineText() {
      return this.bingoCount > 1
        ? `You completed ${this.bingoCount} bingos!`
        : 'You completed a line!';
    },
    claimText() {
      return this.displayPrizes.length > 1
        ? 'Click on the Rewards tab to claim your prizes'
        : 'Click on the Rewards tab to claim your prize';
    }
  },
  methods: {
    randomBetween(min, max) {
      return min + Math.random() * (max - min);
    },
    pickRandom(arr) {
      return arr[Math.floor(Math.random() * arr.length)];
    },
    generateCircles() {
      const colors = [
        '#f99810', '#0f598a', '#e91e63', '#9c27b0', '#ff5722',
        '#00bcd4', '#8bc34a', '#ffeb3b', '#ff9800', '#e53935'
      ];
      return Array.from({ length: 28 }, () => ({
        size: this.randomBetween(6, 22),
        left: this.randomBetween(0, 100),
        duration: this.randomBetween(2.5, 5.5),
        delay: this.randomBetween(0, 2),
        color: this.pickRandom(colors)
      }));
    },
    onPrimaryClick() {
      if (this.continueToBlackout) {
        this.$emit('continue');
        this.$emit('update:open', false);
        return;
      }
      this.goToRewards();
    },
    goToRewards() {
      this.$store.dispatch('cartOrders/resetCart');
      this.$store.dispatch('bingo/clearLastCheckInResult');
      this.$emit('update:open', false);
      this.$nextTick(() => {
        this.$router.push({
          name: 'Rewards',
          query: window.token ? { t: window.token } : {}
        });
      });
    }
  }
};
</script>

<style lang="scss" scoped>
:deep(.v-card-text) {
  color: rgba(0, 0, 0, .6) !important;
  padding-top: 6px !important;
  padding-bottom: 20px !important;
  font-size: 0.875rem !important;
}

.v-dialog {
    width: min(100dvw - 2rem, var(--project-max-width) - 2rem);
    width: 100%;
}

@keyframes glow-pulse {
  0%, 100% {
    box-shadow: 0 0 20px rgba(249, 152, 16, 0.4), 0 0 40px rgba(249, 152, 16, 0.2);
  }
  50% {
    box-shadow: 0 0 30px rgba(249, 152, 16, 0.6), 0 0 60px rgba(249, 152, 16, 0.3);
  }
}

@keyframes fall {
  from {
    transform: translateY(-100%);
    opacity: 1;
  }
  to {
    transform: translateY(100vh);
    opacity: 0.6;
  }
}

.dialog-root {
  position: relative;
}

.dialog-card {
  position: relative;
  z-index: 1;
}

.celebration-circles {
  pointer-events: none;
}

.celebration-circles--fullscreen {
  position: fixed;
  inset: 0;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  z-index: 0;
}

.celebration-circle {
  position: absolute;
  top: -30px;
  border-radius: 50%;
  animation: fall 4s linear infinite;
}

.show-modal-popup {  
  &__card {
    max-width: min(100dvw - 2rem, var(--project-max-width) - 2rem);
    max-height: 94%;
    margin: 0;
    border-radius: 1rem !important;
    animation: glow-pulse 2s ease-in-out infinite;
  }

  h3 {  
    font-size: 1.875rem;
    line-height: 2.25rem;
    margin-bottom: .5rem;
    letter-spacing: -.025em;
    font-weight: 900;
  }
}

:deep(.v-overlay__content) {  
  max-height: calc(100vh - 48px);
  max-width: min(480px, calc(100vw - 48px));
  padding: 2rem;
}

.awesome-btn {
  border-radius: .75rem;
  font-size: 1rem;
  line-height: 1.5rem;
  padding-top: .875rem;
  padding-bottom: .875rem;
  height: auto !important;
  min-height: unset !important;
}
.text-6xl {
    font-size: 3.75rem;
    line-height: 1;
}



.v-dialog > .v-overlay__content .dialog-card {
    padding: 2rem !important;
}

.show-modal-popup h3 {
    color: #1b2632;
    letter-spacing: -.025em;
    font-weight: 900;
    font-size: 1.875rem;
    line-height: 2.25rem;
}

.v-card .v-card-text {
    padding: 0 !important;
    margin: 0 !important;
}

.v-card .v-card-text p {
    color: #737f8c;
    font-size: 1rem !important;
    line-height: 1.5rem !important;
    margin-bottom: .5rem;
}

.prize-earned {
    background: #f9981033;
    padding-left: 1rem !important;
    padding-right: 1rem !important;
    padding-top: .5rem !important;
    padding-bottom: .5rem !important;
    border-radius: 9999px;
    width: fit-content;
    margin: 0 auto 1.5rem;
    display: flex;
    align-items: center;
    gap: .5rem;
}

span.prize-icon {
    font-size: 1.125rem;
    line-height: 1.75rem;
    /* padding-right: .5rem; */
}

span.prize-name {
    color: #f99810;
    font-weight: 700;
    font-size: .875rem;
    line-height: 1.25rem;
}

.v-card p.claim-prize-text {
    color: #737f8c !important;
    font-size: .75rem !important;
    line-height: 1rem !important;
}


.awesome-btn {
    background-color: #0f598a !important;
    font-size: 1rem !important;
    line-height: 1.5rem !important;
}

.v-overlay__content {
    /* padding: 0  !important; */
    margin: 0 auto !important;
}

.v-overlay {
    margin: 0 !important;
}
</style>
